import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../AuthContext';
import { useMutation } from '@tanstack/react-query';
import api from '../api';

function Profile() {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    email: user?.email || '',
    username: user?.username || ''
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
  };

  const closeAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (profileData) => {
      const response = await api.put('/admin-users/profile', profileData);
      return response.data;
    },
    onSuccess: () => {
      setEditMode(false);
      showAlert('Profile updated successfully', 'success');
      // Update the user context if needed
      window.location.reload(); // Simple way to refresh user data
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to update profile', 'error');
    }
  });

  // Update password mutation
  const updatePasswordMutation = useMutation({
    mutationFn: async (passwordData) => {
      console.log('[FRONTEND DEBUG] Password change request:', {
        userId: user.id,
        url: `/admin-users/${user.id}/changecred`,
        data: { ...passwordData, currentPassword: '***', newPassword: '***' }
      });
      
      const response = await api.put(`/admin-users/${user.id}/changecred`, passwordData);
      return response.data;
    },
    onSuccess: () => {
      setPasswordDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showAlert('Password updated successfully', 'success');
    },
    onError: (error) => {
      console.error('[FRONTEND DEBUG] Password change error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        message: error.message
      });
      
      const errorMessage = error.response?.data?.error || 'Failed to update password';
      const statusCode = error.response?.status || 'Unknown';
      showAlert(`${errorMessage} (Status: ${statusCode})`, 'error');
    }
  });

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel editing - reset form
      setProfileForm({
        email: user?.email || '',
        username: user?.username || ''
      });
    }
    setEditMode(!editMode);
  };

  const handleSaveProfile = () => {
    if (!profileForm.email || !profileForm.username) {
      showAlert('Email and username are required', 'error');
      return;
    }
    updateProfileMutation.mutate(profileForm);
  };

  const handlePasswordChange = () => {
    if (!passwordForm.currentPassword) {
      showAlert('Current password is required', 'error');
      return;
    }
    
    if (!passwordForm.newPassword) {
      showAlert('New password is required', 'error');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showAlert('Passwords do not match', 'error');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      showAlert('Password must be at least 8 characters long', 'error');
      return;
    }

    updatePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'admin': return 'warning';
      case 'manager': return 'info';
      case 'analytics': return 'primary';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'manager': return 'Manager';
      case 'analytics': return 'Analytics';
      default: return role;
    }
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon /> My Profile
      </Typography>

      {/* Profile Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Profile Information</Typography>
            <Button
              startIcon={editMode ? <CancelIcon /> : <EditIcon />}
              onClick={handleEditToggle}
              variant={editMode ? "outlined" : "contained"}
              color={editMode ? "secondary" : "primary"}
            >
              {editMode ? 'Cancel' : 'Edit'}
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={profileForm.username}
                onChange={(e) => setProfileForm(prev => ({ ...prev, username: e.target.value }))}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={profileForm.email}
                onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                disabled={!editMode}
                variant={editMode ? "outlined" : "filled"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Role"
                value={getRoleLabel(user.role)}
                disabled
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="body2">Status:</Typography>
                <Chip 
                  label="Active" 
                  color="success" 
                  size="small"
                />
                <Chip 
                  label={getRoleLabel(user.role)} 
                  color={getRoleColor(user.role)} 
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>

          {editMode && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveProfile}
                disabled={updateProfileMutation.isLoading}
              >
                {updateProfileMutation.isLoading ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Security Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon /> Security
          </Typography>
          
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Password
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                ••••••••••••
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setPasswordDialogOpen(true)}
                size="small"
              >
                Change Password
              </Button>
            </Box>
            
            <Divider />
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Last Login
              </Typography>
              <Typography variant="body1">
                {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="Password must be at least 8 characters long"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handlePasswordChange} 
            variant="contained"
            disabled={updatePasswordMutation.isLoading}
          >
            {updatePasswordMutation.isLoading ? <CircularProgress size={20} /> : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={closeAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Profile;